<script setup lang="ts">
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import Multiselect from 'vue-multiselect'
import { computed, defineEmits, defineModel, defineProps } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

defineEmits(['clearValues'])

type SelectId = number | string;

const props = defineProps({
  options: {
    type: Array<{ id: SelectId; text: string; subText?: string; color?: string }>,
    required: true,
    default: []
  },
  label: {
    type: String,
    required: true
  },
  required: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md'
  },
  searchable: {
    type: Boolean,
    default: true
  },
  multiselectRemove: {
    type: Boolean,
    default: true
  }
})

const modelValue = defineModel<SelectId | SelectId[] | null>()

const selectedOptions = computed(() => {
  if (!modelValue.value) return []

  const ids = Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value]

  return props.options.filter((option) => option.id && ids.includes(option.id))
})

const optionsValue = computed(() =>
  props.options.map((o) => ({
    id: o.id,
    text: o.text,
    subText: o.subText || '',
    color: o.color
  }))
)

function textLabel(option: { text: string; subText?: string }) {
  return option.subText ? `${option.text} (${option.subText})` : option.text
}

function handleModelUpdate(val: { id: SelectId } | { id: SelectId }[] | null) {
  if (props.multiple) {
    modelValue.value = val && Array.isArray(val) ? val.map((item) => item.id) : []
  } else {
    modelValue.value = val && !Array.isArray(val) ? val.id : null
  }
}

function getColorClass(color?: string) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    gray: 'bg-gray-500',
    red: 'bg-red-500',
    teal: 'bg-teal-500'
  }
  return color ? colors[color] || 'bg-gray-400' : ''
}

function getTagClass(color?: string) {
  const classes: Record<string, string> = {
    blue: '!bg-blue-600 !text-white',
    green: '!bg-green-600 !text-white',
    orange: '!bg-orange-600 !text-white',
    gray: '!bg-gray-600 !text-white',
    red: '!bg-red-600 !text-white',
    teal: '!bg-teal-600 !text-white'
  }
  return color ? classes[color] || '' : ''
}
</script>

<template>
  <label :class="['form-control w-full', size === 'sm' ? 'max-w-xs' : '']" :for="label">
    <div class="label mb-1 flex-row items-center justify-between py-1">
      <span class="label-text font-semibold">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </span>

      <div class="tooltip" data-tip="Tout retirer">
        <component
          v-if="clearable"
          :is="XMarkIcon"
          class="shrink-0 h-4 w-4 cursor-pointer"
          @click="$emit('clearValues')"
        />
      </div>
    </div>

    <Multiselect
      :model-value="selectedOptions"
      @update:model-value="handleModelUpdate"
      :options="optionsValue"
      :multiple="multiple"
      :required="required"
      :searchable="searchable"
      :placeholder="'Sélectionner ' + label + '...'"
      select-label="Sélectionner"
      selected-label="Sélectionné"
      deselect-label="Retirer"
      label="text"
      track-by="id"
      :custom-label="textLabel"
      :disabled="disabled"
    >
      <template #option="{ option }">
        <div class="flex items-center space-x-4">
          <div v-if="option.color" :class="['w-3 h-3 rounded-full', getColorClass(option.color)]"></div>
          <div class="w-full">
            <p class="font-bold">{{ option.text }}</p>
            <p class="text-sm">{{ option.subText }}</p>
          </div>
        </div>
      </template>

      <template #singleLabel="{ option }">
        <div class="flex items-center space-x-2">
          <div v-if="option.color" :class="['w-2 h-2 rounded-full', getColorClass(option.color)]"></div>
          <span>{{ option.text }}</span>
        </div>
      </template>

      <template #tag="{ option, remove }">
        <div :class="[
            'multiselect__tag flex items-center space-x-1',
            option.color ? getTagClass(option.color) : '',
            multiselectRemove ? '' : 'cursor-pointer no-icon',
          ]">
          <span>{{ option.text }}</span>
          <i v-if="multiselectRemove" class="multiselect__tag-icon" @click="remove(option)"></i>
        </div>
      </template>

      <template #noOptions>
        <div class="p-2">Aucun résultat trouvé</div>
      </template>
      <template #noResult>
        <div class="p-2">Aucune option disponible</div>
      </template>
    </Multiselect>
  </label>
</template>
<style scoped>
.multiselect__tag-icon {
  margin-top: -2px;
}
</style>
